// Local state hook for transport layer toggles.
// Mirrors use-map-poi-state.ts shape for consistency.
// Toggle state is React local state ONLY — NOT in URL, NOT in shared filters, NOT in localStorage.
import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import { transportLayerQueries } from '../api/queries';
import type { WithCallWithAuth } from '../api/queries';
import type { TransportLineBboxResponse, NearbyTransportResponse } from '@ooh/contracts';
import { useDebouncedBbox, parseBbox, ZERO_BBOX } from '../../../hooks/use-debounced-bbox';

// ─── Sub-hook: active scopes param ───────────────────────────────────────────

function useActiveScopesParam(isPublicEnabled: boolean, isRoadsEnabled: boolean) {
  return React.useMemo<string | undefined>(() => {
    const parts: string[] = [];
    if (isPublicEnabled) parts.push('public');
    if (isRoadsEnabled) parts.push('roads');
    return parts.length > 0 ? parts.join(',') : undefined;
  }, [isPublicEnabled, isRoadsEnabled]);
}

// ─── Sub-hook: transport lines query ─────────────────────────────────────────

interface UseTransportLinesOptions {
  auth: WithCallWithAuth;
  bbox: string | null;
  activeScopesParam: string | undefined;
}

function useTransportLinesQuery({ auth, bbox, activeScopesParam }: UseTransportLinesOptions) {
  const debouncedBbox = useDebouncedBbox(bbox, 300);
  const parsedBbox = debouncedBbox !== null ? parseBbox(debouncedBbox) : null;
  const coords = parsedBbox ?? ZERO_BBOX;
  const enabled = activeScopesParam !== undefined && parsedBbox !== null;
  return useQuery({
    ...transportLayerQueries.listByBbox(auth, { ...coords, scopes: activeScopesParam }),
    enabled,
    staleTime: 5 * 60 * 1000,
    placeholderData: (prev) => prev,
  });
}

// ─── Sub-hook: nearby transport query ────────────────────────────────────────

interface UseNearbyTransportOptions {
  auth: WithCallWithAuth;
  selectedMediaUnitId: number | null | undefined;
  activeScopesParam: string | undefined;
}

function useNearbyTransportQuery({
  auth,
  selectedMediaUnitId,
  activeScopesParam,
}: UseNearbyTransportOptions) {
  const enabled = activeScopesParam !== undefined && selectedMediaUnitId != null;
  return useQuery({
    ...transportLayerQueries.nearbyTransport(auth, selectedMediaUnitId ?? 0),
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

// ─── Types ────────────────────────────────────────────────────────────────────

export interface UseTransportLayerStateOptions {
  auth: WithCallWithAuth;
  bbox: string | null;
  selectedMediaUnitId?: number | null;
}

export interface UseTransportLayerStateResult {
  /** Whether the public transport layer toggle is on */
  isPublicEnabled: boolean;
  /** Whether the major roads layer toggle is on */
  isRoadsEnabled: boolean;
  /** Toggle the public transport layer on/off */
  togglePublic: () => void;
  /** Toggle the major roads layer on/off */
  toggleRoads: () => void;
  /** Comma-separated active scopes string for query params, or undefined when none active */
  activeScopesParam: string | undefined;
  /** Transport line features for the current debounced viewport */
  transportLines: TransportLineBboxResponse | undefined;
  /** Whether transport line data is loading */
  isTransportLinesLoading: boolean;
  /** Nearby transport context for the selected media unit */
  nearbyTransport: NearbyTransportResponse | undefined;
}

// ─── Main hook ────────────────────────────────────────────────────────────────

export function useTransportLayerState({
  auth,
  bbox,
  selectedMediaUnitId,
}: UseTransportLayerStateOptions): UseTransportLayerStateResult {
  // Local state — NOT in URL, NOT in shared filters
  const [isPublicEnabled, setIsPublicEnabled] = React.useState(false);
  const [isRoadsEnabled, setIsRoadsEnabled] = React.useState(false);

  const togglePublic = React.useCallback(() => setIsPublicEnabled((prev) => !prev), []);
  const toggleRoads = React.useCallback(() => setIsRoadsEnabled((prev) => !prev), []);

  const activeScopesParam = useActiveScopesParam(isPublicEnabled, isRoadsEnabled);

  const { data: transportLines, isLoading: isTransportLinesLoading } = useTransportLinesQuery({
    auth,
    bbox,
    activeScopesParam,
  });

  const { data: nearbyTransport } = useNearbyTransportQuery({
    auth,
    selectedMediaUnitId,
    activeScopesParam,
  });

  return {
    isPublicEnabled,
    isRoadsEnabled,
    togglePublic,
    toggleRoads,
    activeScopesParam,
    transportLines: transportLines as TransportLineBboxResponse | undefined,
    isTransportLinesLoading,
    nearbyTransport: nearbyTransport as NearbyTransportResponse | undefined,
  };
}
